import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { AutoSizer } from 'react-virtualized';
import { useDispatch, useSelector } from 'react-redux';
import { MdAutoFixHigh } from 'react-icons/md';
import { useSnackbar } from 'notistack';

import { Action, Role } from 'helpers';
import AssetCard from './components/AssetCard';
import { EnhancedTableToolbar, VirtualGrid } from 'components';
import { createLoadedDataObject } from '../GridView/TableGrid';
import { sortBy } from 'GeminiViewerComponent/helpers/lodashUtils';
import { AssetTypes } from 'GeminiViewerComponent/helpers';
import PopupAction from 'components/Misc/PopupActions/components/PopupAction';
import { fetchMoreAssets, selectAllAssets } from 'features/assets/assetsTableSlice';
import { accountsSlice } from 'app/store';
import { procedureService } from 'features/services';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';

const noop = () => {};

export default function CardView({
    assets,
    pageInfo,
    searchString,
    isLoadingAssets,
    assetType,
    assetPayload,
    readOnly,
    headerDisplay = true,
    accordianGrid = false,
    categoryId = null,
    setFilteredAssets = noop,
}) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const rowHeight = 240;

    const { selectActiveUser } = accountsSlice;

    const [selected, setSelected] = useState([]);
    const [selectedAll, setSelectedAll] = useState(false);
    const [cardWrapperHeight, setCardWrapperHeight] = useState(null);
    const allRows = useSelector(selectAllAssets);
    const [filteredloadedRowsMap, setFilteredloadedRowsMap] = useState([]);
    const loadedRowsMap = useSelector((state) => state.assets.loadedRowsMap);
    const activeUser = useSelector(selectActiveUser);

    const parentRef = useRef(null);
    // Pagination for category view: fixed page size and per-category tracking
    const PAGE_SIZE = 15;
    const pagesLoadedRef = useRef(new Set()); // pages already loaded (pageIndex)
    const pendingPagesRef = useRef(new Set()); // pages currently in flight (pageIndex)

    // When category changes, reset per-category pagination state so new category starts from 0
    useEffect(() => {
        pagesLoadedRef.current.clear();
        pendingPagesRef.current.clear();
        setFilteredloadedRowsMap(createLoadedDataObject(0));
    }, [categoryId, setFilteredAssets]);

    const handleBatchRemoveHeaderRows = useCallback(async () => {
        const selectedAssetsMap = new Map((allRows ?? []).map((asset) => [asset.asset_id, asset]));
        const procedureIds = selected
            .map((assetId) => selectedAssetsMap.get(assetId))
            .filter((asset) => Number(asset?.asset_type_id) === AssetTypes.Procedure && asset?.procedure_id)
            .map((asset) => asset.procedure_id);

        const uniqueProcedureIds = Array.from(new Set(procedureIds));

        if (uniqueProcedureIds.length === 0) {
            enqueueSnackbar('No procedures selected to process.', {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'info',
            });
            return;
        }

        dispatch(activateLoading());
        try {
            const response = await procedureService.removeHeaderRows(uniqueProcedureIds);
            if (response.updatedNodes > 0) {
                enqueueSnackbar(`${response.message} Procedures updated: ${response.procedureIds?.length ?? 0}.`, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: 'success',
                });
            } else {
                enqueueSnackbar(`${response.message}`, {
                    action: (key) => <SnackbarDismiss key={key} />,
                    variant: 'info',
                });
            }
        } catch (e) {
            enqueueSnackbar(e?.message ?? 'Failed to remove header rows.', {
                action: (key) => <SnackbarDismiss key={key} />,
                variant: 'error',
            });
        } finally {
            dispatch(deactivateLoading());
        }
    }, [allRows, selected, dispatch, enqueueSnackbar]);

    function usePrevious(value) {
        const ref = useRef();
        useEffect(() => {
            ref.current = value;
        });
        return ref.current;
    }

    /* selection start */
    const isSelected = (id) => selected.indexOf(id) !== -1;

    const prevSelectedAll = usePrevious(selectedAll);
    if (prevSelectedAll && selectedAll) {
        if (categoryId || categoryId === 0) {
            if (selected.length !== assets?.length) {
                setSelected(assets.map((row) => row.asset_id));
                setSelectedAll(true);
            }
        } else {
            if (selected.length !== allRows?.length) {
                setSelected(allRows.map((row) => row.asset_id));
                setSelectedAll(true);
            }
        }
    }

    const handleClick = useCallback(
        (event, asset_id) => {
            event.stopPropagation();
            const selectedIndex = selected.indexOf(asset_id);
            let newSelected = [];

            if (selectedIndex === -1) {
                newSelected = newSelected.concat(selected, asset_id);
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(selected.slice(1));
            } else if (selectedIndex === selected.length - 1) {
                newSelected = newSelected.concat(selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
            }
            setSelected(newSelected);
            if (categoryId || categoryId === 0) {
                setSelectedAll(newSelected.length === assets?.length);
            } else {
                setSelectedAll(newSelected.length === allRows?.length);
            }
        },
        [selected, categoryId, assets, allRows]
    );

    return (
        !isLoadingAssets && (
            <>
                {selected.length > 0 && !readOnly && Role?.PublishRoles?.includes(activeUser?.role) && (
                    <EnhancedTableToolbar
                        numSelected={selected.length}
                        totalCount={pageInfo.TotalCount}
                        toolTipTitle={'Publish'}
                    >
                        <Tooltip title="Publish">
                            <PopupAction
                                action={Action.PublishArray}
                                object={selected}
                                level={'asset'}
                                showLabel={false}
                            />
                        </Tooltip>
                    </EnhancedTableToolbar>
                )}
                <div
                    style={{
                        marginTop: `${!headerDisplay ? '0px' : selected.length > 0 && !readOnly ? 0 : '60px'}`,
                    }}
                    ref={parentRef}
                >
                    <div
                        style={{
                            height: `${
                                cardWrapperHeight
                                    ? cardWrapperHeight + 'px'
                                    : `calc(100vh - 310px ${
                                          !headerDisplay ? (selected.length > 0 ? '+ 0px' : '+ 60px') : '+ 0px'
                                      })`
                            }`,
                        }}
                    >
                        <AutoSizer>
                            {({ width, height }) => (
                                <VirtualGrid
                                    initialRows={assets}
                                    allRows={allRows}
                                    totalRowCount={pageInfo.TotalCount}
                                    loadedRowsMap={
                                        categoryId || categoryId === 0 ? filteredloadedRowsMap : loadedRowsMap
                                    }
                                    loadMoreRows={loadMoreRows}
                                    width={width}
                                    height={height}
                                    rowHeight={rowHeight}
                                    cellRenderer={cellRenderer}
                                    parentRef={parentRef}
                                    style={{
                                        overflowX: 'hidden',
                                    }}
                                    accordianGrid={accordianGrid}
                                    setCardWrapperHeight={setCardWrapperHeight}
                                />
                            )}
                        </AutoSizer>
                    </div>
                </div>
            </>
        )
    );

    async function loadMoreRows({ startIndex, stopIndex }) {
        // Non-category (global) behavior unchanged: use grid indices directly
        if (!(categoryId || categoryId === 0)) {
            const updatedStopIndex = stopIndex < 14 ? 14 : stopIndex;
            return dispatch(
                fetchMoreAssets({
                    startIndex: startIndex,
                    stopIndex: updatedStopIndex,
                    searchString: searchString,
                    ...assetPayload,
                })
            );
        }

        // CATEGORY VIEW: request next backend page based on how many assets are already loaded
        // Do NOT use grid startIndex directly as backend offset.
        const currentLoadedCount = Array.isArray(assets) ? assets.length : 0;
        const nextStart = currentLoadedCount; // next offset in backend
        const pageIndex = Math.floor(nextStart / PAGE_SIZE);

        // If this page already loaded or already pending, skip
        if (pagesLoadedRef.current.has(pageIndex)) {
            return Promise.resolve();
        }
        if (pendingPagesRef.current.has(pageIndex)) {
            return Promise.resolve();
        }

        pendingPagesRef.current.add(pageIndex);
        try {
            const backendStart = nextStart;
            const backendStop = backendStart + PAGE_SIZE - 1;
            const loadedAssetsMap = new Map();
            const moreCategoriesAssets = await dispatch(
                fetchMoreAssets({
                    startIndex: backendStart,
                    stopIndex: backendStop,
                    searchString: searchString,
                    assetCategoryId: categoryId,
                    ...assetPayload,
                })
            );

            const newAssets = moreCategoriesAssets.payload?.assets ?? [];
            if (newAssets.length > 0) {
                // Merge deduplicated by asset_id preserving ordering rules used before
                newAssets.forEach((a) => loadedAssetsMap.set(a.asset_id, a));
                // start with existing assets
                (assets ?? []).forEach((a) => loadedAssetsMap.set(a.asset_id, a));

                const merged = [
                    ...Array.from(loadedAssetsMap.values())
                        .sort(sortBy('display_name', 'DESC'))
                        .sort(sortBy('is_pinned', 'ASC')),
                ];

                setFilteredAssets(merged);
                setFilteredloadedRowsMap(createLoadedDataObject(merged.length));
            } else {
                // No more assets for this page/category -> ensure UI knows (avoid infinite retries)
                setFilteredloadedRowsMap(createLoadedDataObject(currentLoadedCount));
            }

            // Mark page as loaded (even if empty) so we won't re-request it
            pagesLoadedRef.current.add(pageIndex);
        } finally {
            pendingPagesRef.current.delete(pageIndex);
        }
    }

    function cellRenderer(gridItem, key, style, columnWidth) {
        if (gridItem == null) {
            return null;
        }
        return (
            <div style={style} key={key}>
                <AssetCard
                    asset={gridItem}
                    width={columnWidth - 20}
                    assetType={assetType}
                    handleClick={handleClick}
                    content={gridItem}
                    cellLevel={'asset'}
                    selected={selected}
                    isSelected={isSelected}
                    readOnly={readOnly}
                />
            </div>
        );
    }
}